import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";
import Breadcrumbs from "../../components/shared/Breadcrumbs";
import { SECTION_BASE } from "../../constants";
import { useDemo } from "../../contexts/DemoContext";
import { useLayout } from "../../contexts/LayoutContext";
import { LAYOUT } from "../../constants/layout";

const SubPageLayout = ({
  children,
  title,
  subtitle,
  hideBreadcrumbs,
  actionButton,
  breadcrumbItems,
  className = "",
  isSidebarExpanded,
  hideHeaderBorder = false,
}) => {
  const location = useLocation();
  const isHomeDashboard = location.pathname === "/home-dashboard";
  const { isDemoMode, isPaused } = useDemo();
  const { rightSidebarWidth } = useLayout();

  // Calculate the sidebar width - only use actual measured width when expanded
  const sidebarMargin = isSidebarExpanded ? Math.max(rightSidebarWidth, parseInt(LAYOUT.sidebarWidth)) : 0;

  // Event trap that allows scrolling but blocks clicks during demo
  const handleDemoTrap = useCallback((e) => {
    if (isDemoMode) {
      const targetEl = e.target instanceof Element ? e.target : e.target?.parentElement;

      // Allow spotlight targets / explicitly-allowed targets / modal clicks
      const isAllowed =
        !!targetEl?.closest?.('[data-allow-demo-click="true"]') ||
        !!targetEl?.closest?.('[data-spotlight-target]') ||
        !!targetEl?.closest?.('[data-spotlight-active="true"]') ||
        !!targetEl?.closest?.('.demo-modal, [role="dialog"], .Modal, .modal, [data-demo-modal]');

      // Log for debugging
      console.log('🎯 [SubPageLayout] Click:', {
        isAllowed,
        target: targetEl,
        path: e.composedPath?.()?.map(el => ({
          className: el.className,
          id: el.id,
          tagName: el.tagName
        }))
      });

      if (isAllowed) {
        return;
      }

      // Block all other clicks during demo mode
      console.log('🛑 [SubPageLayout] Blocking non-spotlight click during demo');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      e.cancelBubble = true;
      e.returnValue = false;
      return false;
    }
  }, [isDemoMode]);

  return (
    <div 
      className="flex flex-col min-h-0 h-full bg-white dark:bg-neutral-900 overflow-x-hidden"
      style={{
        transition: "padding-right 0.3s ease-in-out",
        paddingRight: isSidebarExpanded ? `${sidebarMargin}px` : 0,
        ...(isDemoMode && {
          cursor: 'default'  // Show default cursor to indicate scrollability
        })
      }}
    >
      <div className={`flex flex-col flex-1 min-h-0 px-3 md:px-4 lg:px-5 pb-0 ${className}`}>
        <div className="pt-2">
          {!hideBreadcrumbs && (
            <div className="mb-2 text-[10px] lg:text-xs">
              <Breadcrumbs items={breadcrumbItems} />
            </div>
          )}
          <div className="pb-3 md:pb-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-lg md:text-xl font-medium text-neutral-900 dark:text-neutral-100">
                  {title}
                </h1>
                {subtitle && (
                  <p className="mt-0.5 text-xs md:text-sm text-neutral-500 dark:text-neutral-400">
                    {subtitle}
                  </p>
                )}
              </div>
              {actionButton && <div>{actionButton}</div>}
            </div>
          </div>
        </div>

        <div className="flex-1 bg-white dark:bg-neutral-900 overflow-y-auto hide-scrollbar w-full h-full">
          <div
            className={`pt-2 md:pt-2 lg:pt-3 ${SECTION_BASE}`}
            style={{
              pointerEvents: "auto",  // Keep pointer-events enabled to allow scrolling
              userSelect: isDemoMode ? "none" : "auto",  // Prevent text selection during demo mode
              cursor: isDemoMode ? 'default' : 'auto'  // Show default cursor to indicate scrollability
            }}
            onClick={handleDemoTrap}
            onWheel={(e) => {
              if (isDemoMode) {
                // Allow wheel events to propagate for scrolling
                e.stopPropagation();
              }
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubPageLayout;